import { slugify } from '@/utils/string.js';

/**
 * Enum for transfer statuses
 * @enum {string}
 * @readonly
 */
export const TransferStatus = Object.freeze({
  CREATED: 'CREATED',
  SUBMITTED: 'SUBMITTED',
  VERIFIED: 'VERIFIED',
  FAILED_VERIFICATION: 'FAILED_VERIFICATION',
  RELEASED: 'RELEASED',
  SETTLED: 'SETTLED',
  CANCELED: 'CANCELED',
});

/**
 * Enum for transfer status badge text
 * @enum {string}
 * @readonly
 */
export const TransferStatusBadgeCopy = Object.freeze({
  [TransferStatus.CREATED]: 'Pending',
  [TransferStatus.SUBMITTED]: 'Pending',
  [TransferStatus.VERIFIED]: 'Ready to pay',
  [TransferStatus.FAILED_VERIFICATION]: 'Action required',
  [TransferStatus.RELEASED]: 'Sent',
  [TransferStatus.SETTLED]: 'Received',
  [TransferStatus.CANCELED]: 'Canceled',
});

/**
 * Enum for agency transfer status badge text
 * @enum {string}
 * @readonly
 */
export const AgencyTransferStatusBadgeCopy = Object.freeze({
  [TransferStatus.RELEASED]: 'Incoming',
  [TransferStatus.SETTLED]: 'Completed',
  [TransferStatus.CANCELED]: 'Failed',
});

/**
 * Enum for transfer status badge color
 * @enum {string}
 * @readonly
 */
export const TransferStatusBadgeColor = Object.freeze({
  [TransferStatus.CREATED]: 'neutral',
  [TransferStatus.SUBMITTED]: 'neutral',
  [TransferStatus.VERIFIED]: 'primary',
  [TransferStatus.FAILED_VERIFICATION]: 'warning',
  [TransferStatus.RELEASED]: 'success',
  [TransferStatus.SETTLED]: 'success',
  [TransferStatus.CANCELED]: 'error',
});

/**
 * Enum for transfer status icon.
 * These icon choices mirror the visual language already used for statement statuses
 * (e.g. loading for a pending state, check for a settled/successful state) and are a
 * reasonable default pending design sign-off.
 * @enum {string}
 * @readonly
 */
export const TransferStatusIcon = Object.freeze({
  [TransferStatus.CREATED]: 'loading',
  [TransferStatus.SUBMITTED]: 'send-three',
  [TransferStatus.VERIFIED]: 'check-square',
  [TransferStatus.FAILED_VERIFICATION]: 'alert-circle',
  [TransferStatus.RELEASED]: 'building-eight',
  [TransferStatus.SETTLED]: 'check',
  [TransferStatus.CANCELED]: 'info-circle',
});

/**
 * Groups transfer statuses by their badge copy - some statuses share one label (e.g.
 * CREATED/SUBMITTED are both "Pending"), so a single selected group can span multiple raw
 * statuses. Shared by the filters drawer's status pills and the active filter badge, so
 * "how many statuses are selected" is always counted in groups, not raw values.
 * @param {boolean} isAgency
 * @returns {Array<{label: string, values: string[], icon: string, color: string}>}
 */
export const getTransferStatusGroups = (isAgency) => {
  const statusBadgeCopy = isAgency ? AgencyTransferStatusBadgeCopy : TransferStatusBadgeCopy;

  return Object.keys(statusBadgeCopy).reduce((groups, status) => {
    const label = statusBadgeCopy[status];
    const group = groups.find((g) => g.label === label);

    if (group) {
      group.values.push(status);
    } else {
      groups.push({
        label,
        values: [status],
        icon: TransferStatusIcon[status],
        color: TransferStatusBadgeColor[status],
      });
    }

    return groups;
  }, []);
};

/**
 * Encodes selected raw transfer statuses as the role-aware, snake_case labels the user picked,
 * for use in the URL query string (e.g. `RELEASED` -> `sent` for a supplier, `incoming` for an
 * agency). Statuses that share a label (e.g. CREATED/SUBMITTED are both "Pending") collapse to
 * one entry, since that's how the drawer's pills and the active filter badge already group them.
 * @param {string[]} statuses
 * @param {boolean} isAgency
 * @returns {string[]}
 */
export const encodeTransferStatusesForUrl = (statuses, isAgency) => {
  const groups = getTransferStatusGroups(isAgency);
  const labels = new Set();

  statuses.forEach((status) => {
    const group = groups.find((g) => g.values.includes(status));
    if (group) {
      labels.add(slugify(group.label));
    }
  });

  return [...labels];
};

/**
 * Reverses encodeTransferStatusesForUrl: maps snake_case label slugs from the URL back to their
 * underlying raw status values. Unrecognized slugs are silently dropped.
 * @param {string|string[]} value
 * @param {boolean} isAgency
 * @returns {string[]}
 */
export const decodeTransferStatusesFromUrl = (value, isAgency) => {
  const slugs = Array.isArray(value) ? value : [value];
  const groups = getTransferStatusGroups(isAgency);

  return slugs.flatMap((slug) => groups.find((g) => slugify(g.label) === slug)?.values ?? []);
};

const SHARED_TRANSFER_FILTER_KEYS = ['status', 'date', 'date_from', 'date_to'];
const AGENCY_TRANSFER_FILTER_KEYS = ['recipient_currency', 'sender_country'];
const SUPPLIER_TRANSFER_FILTER_KEYS = ['statement_id', 'sender_currency', 'recipient_country'];

/**
 * Filter keys available to the current role's sidebar - used to scope which keys
 * useFilterUrlSync is allowed to hydrate from the URL/sessionStorage into the transfers store,
 * so a crafted URL can't apply a filter field the user's sidebar doesn't offer them.
 * @param {boolean} isAgency
 * @returns {string[]}
 */
export const getTransferFilterKeys = (isAgency) => [
  ...SHARED_TRANSFER_FILTER_KEYS,
  ...(isAgency ? AGENCY_TRANSFER_FILTER_KEYS : SUPPLIER_TRANSFER_FILTER_KEYS),
];

/**
 * Enum for transfer file types
 * @enum {string}
 * @readonly
 */
export const TransferFileTypes = Object.freeze({
  INVOICE: 'INVOICE',
  REMITTANCE: 'REMITTANCE',
});

/**
 * Enum for transfer file statuses
 * @enum {string}
 * @readonly
 */
export const TransferFileStatus = Object.freeze({
  PROCESSING: 'PROCESSING',
  FAILED: 'FAILED',
  SUCCEEDED: 'SUCCEEDED',
});
